import {
  cloneListOrNull, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData,
} from '../../models/dto/dto.js';

import dayjs from 'dayjs';
import { Price } from '../../models/serializable/price.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../../models/core/version-range.js';

export class TicketGiftCardComponent extends Component {
  static readonly TYPE_ID: string = 'b54ebc0354ca41c5b900823ebab8a5ab';
  static readonly SCHEMA_VERSION: number = 5;

  private constructor(
    public readonly includedEvents: string[] | null,
    public readonly excludedEvents: string[] | null,
    public readonly value: Price,
    public readonly startDate: dayjs.Dayjs | null,
    public readonly expirationDate: dayjs.Dayjs | null,
  ) {
    super();
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return TicketGiftCardComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(4, TicketGiftCardComponent.SCHEMA_VERSION);
  }

  static create(args: {
    includedEvents: string[] | null,
    excludedEvents: string[] | null,
    value: Price,
    startDate: dayjs.Dayjs | null,
    expirationDate: dayjs.Dayjs | null,
  }): TicketGiftCardComponent {
    return new TicketGiftCardComponent(
        args.includedEvents,
        args.excludedEvents,
        args.value,
        args.startDate,
        args.expirationDate,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketGiftCardComponent {
    return new TicketGiftCardComponent(
        MapUtils.getListOrNull<string>(map, 'includedEvents', (e) => e as string),
        MapUtils.getListOrNull<string>(map, 'excludedEvents', (e) => e as string),
        Price.decode(MapUtils.get<string>(map, 'value')),
        TimeZone.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'startDate')),
        TimeZone.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'expirationDate')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'includedEvents': this.includedEvents,
      'excludedEvents': this.excludedEvents,
      'value': this.value.encode(),
      'startDate': TimeZone.encodeDateOrNull(this.startDate),
      'expirationDate': TimeZone.encodeDateOrNull(this.expirationDate),
    };
  }

  clone(): this {
    return new TicketGiftCardComponent(
        cloneListOrNull(this.includedEvents),
        cloneListOrNull(this.excludedEvents),
        this.value.clone(),
        TimeZone.cloneDateOrNull(this.startDate),
        TimeZone.cloneDateOrNull(this.expirationDate),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.includedEvents, this.includedEvents) &&
        isSameList(other.excludedEvents, this.excludedEvents) &&
        isSameData(other.value, this.value) &&
        other.startDate === this.startDate &&
        other.expirationDate === this.expirationDate;
  }
}

export class TicketGiftCardComponentBuilder extends DataModelBuilder<TicketGiftCardComponent> {
  build(context: SerializationContext, map: SerializedData): TicketGiftCardComponent {
    return TicketGiftCardComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TicketGiftCardComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(TicketGiftCardComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added `expirationDate` of type `dayjs.Dayjs | null`
    if(version < 2) version = 2;

    // In this version we added `startDate` of type `dayjs.Dayjs | null`
    if(version < 3) version = 3;

    if(version < 4) {
      map['includedEvents'] = map['eventId'];
      version = 4;
    }

    if(version < 5) version = 5;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
